import { arrayMove } from "@dnd-kit/sortable";
import { zodResolver } from "@hookform/resolvers/zod";
import { isEmpty } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { v4 } from "uuid";
import { CHECKLIST_QUESTION_TYPE } from "../constants";
import { ChecklistSchema, ChecklistType } from "../utilities/checkListsSchema";
import {
  useCreateCheckListMutation,
  useGetCheckListById,
  useGetCheckListType,
  useUpdateCheckListMutation
} from "./useCheckListMutation";

export const useChecklistDialog = (isUpdateMode?: boolean) => {
  const params = useParams<{ checklistId: string }>();
  const navigate = useNavigate();

  const { listQuestionTypes } = useGetCheckListType(true);

  const onClose = useCallback(() => {
    navigate(`/settings/checklists`);
  }, [navigate]);

  const defaultValues: ChecklistType = useMemo(
    () => ({
      checklist: {
        name: "",
        description: "",
        sections: [
          {
            id: v4(),
            title: "",
            description: "",
            displayOrder: 0,
            questions: [
              {
                id: v4(),
                checklistQuestionTypeId: listQuestionTypes?.questionTypes[0]?.id || "",
                questionTypeCode: CHECKLIST_QUESTION_TYPE.MULTIPLE_CHOICE,
                questionText: "",
                displayOrder: 0,
                isRequired: false,
                options: [{ id: v4(), optionText: "", displayOrder: 0 }]
              }
            ]
          }
        ]
      }
    }),
    [listQuestionTypes]
  );

  const form = useForm<ChecklistType>({
    resolver: zodResolver(ChecklistSchema),
    defaultValues,
    mode: "onSubmit"
  });

  const {
    control,
    handleSubmit,
    watch,
    setFocus,
    trigger,
    formState: { dirtyFields, errors }
  } = form;

  const [selectedSectionIndex, setSelectedSectionIndex] = useState<number>(0);
  const [openPreviewDialog, setOpenPreviewDialog] = useState<boolean>(false);
  const [activeId, setActiveId] = useState<string>("");
  const [resetSeq, setResetSeq] = useState<number>(0);

  const sectionsArray = useFieldArray({ control, name: "checklist.sections" });
  const sections = watch("checklist.sections");
  const checklists = watch("checklist");

  const isChangedForm = !isEmpty(dirtyFields);
  const disableAddSection = sections?.length >= 20;
  const checklistId = params.checklistId;

  const handleAddSection = () => {
    const newIndex = sections.length || 0;
    sectionsArray.append({
      id: v4(),
      title: "",
      description: "",
      displayOrder: newIndex,
      questions: [
        {
          id: v4(),
          checklistQuestionTypeId: listQuestionTypes?.questionTypes[0]?.id || "",
          questionTypeCode: listQuestionTypes?.questionTypes[0]?.code || "",
          questionText: "",
          displayOrder: 0,
          isRequired: false,
          options: [{ id: v4(), optionText: "", displayOrder: 0 }]
        }
      ]
    });
    setSelectedSectionIndex(newIndex);
    triggerValidationValueDebounced();
    setFocus(`checklist.sections.${sections?.length}.title`);
  };

  const handleRemoveSection = (sectionIndex: number) => {
    const currentSections = sections || [];
    const remainingSections = currentSections
      .filter((_: any, idx: number) => idx !== sectionIndex)
      .map((section: any, idx: number) => ({ ...section, displayOrder: idx }));
    sectionsArray.replace(remainingSections as any);
    setSelectedSectionIndex(0);
    triggerValidationValueDebounced();
  };

  const resetForm = () => {
    form.reset(defaultValues, {
      keepDirty: false,
      keepTouched: false,
      keepIsSubmitted: false,
      keepSubmitCount: false,
      keepErrors: false
    } as any);

    sectionsArray.replace(defaultValues.checklist.sections as any);

    setSelectedSectionIndex(0);
    setResetSeq((x) => x + 1);
  };

  const { createCheckList } = useCreateCheckListMutation(onClose, resetForm);
  const { updateCheckList } = useUpdateCheckListMutation(onClose);

  // Keep original data for diffing
  const [originalChecklist, setOriginalChecklist] = useState<any | null>(null);

  const buildCreatePayload = (data: ChecklistType) => ({
    checklist: {
      ...(checklistId ? { checklistId: checklistId } : {}),
      ...data.checklist,
      sections: (data.checklist.sections || []).map((section, sectionIndex) => {
        const { id: _sectionId, ...sectionRest } = section as any;
        return {
          ...sectionRest,
          displayOrder: sectionIndex,
          questions: (section.questions || []).map((question, questionIndex) => {
            const { id: _questionId, ...questionRest } = question as any;
            return {
              ...questionRest,
              displayOrder: questionIndex,
              options: (question.options || []).map((option, optionIndex) => {
                const { id: _optionId, ...optionRest } = option as any;
                return {
                  ...optionRest,
                  displayOrder: optionIndex
                };
              })
            };
          })
        };
      })
    }
  });

  const onSubmit = (data: ChecklistType) => {
    if (isUpdateMode && originalChecklist) {
      const payload = buildCreatePayload(data);
      updateCheckList(payload as any);
      return;
    }
    const payload = buildCreatePayload(data);
    createCheckList(payload as any);
  };

  const handleFormSubmit = (e?: React.BaseSyntheticEvent) => {
    e?.preventDefault();
    return handleSubmit(onSubmit)(e);
  };

  const isError = Object.keys(errors).length > 0;

  const hasSectionError = useCallback(
    (sectionIndex: number) => {
      const sectionsErrors = errors?.checklist?.sections;
      return Boolean(sectionsErrors && sectionsErrors[sectionIndex]);
    },
    [errors]
  );

  const handleSectionDragEnd = (activeIndex: number, overIndex: number) => {
    const reorderedSections = arrayMove(sections, activeIndex, overIndex);
    const finalSections = reorderedSections.map((section: any, index: number) => ({
      ...section,
      displayOrder: index
    }));
    sectionsArray.replace(finalSections as any);

    if (selectedSectionIndex === activeIndex) {
      setSelectedSectionIndex(overIndex);
    } else if (selectedSectionIndex === overIndex) {
      setSelectedSectionIndex(activeIndex);
    }
  };

  //API Calls
  const { checklist: checklistData, isLoading } = useGetCheckListById(checklistId || '', isUpdateMode);

  useEffect(() => {
    if (checklistData) {
      form.reset({
        checklist: checklistData as any
      });
      setOriginalChecklist(checklistData as any);
    }
  }, [checklistData, form]);

  const debouncedValidationRef = useRef<NodeJS.Timeout | null>(null);
  const triggerValidationValueDebounced = useCallback(() => {
    if (debouncedValidationRef.current) {
      clearTimeout(debouncedValidationRef.current);
    }
    debouncedValidationRef.current = setTimeout(() => {
      trigger();
    }, 300);
  }, [trigger]);

  return {
    // Form management
    form,
    control,
    watch,
    handleFormSubmit,
    triggerValidationValueDebounced,
    // State
    selectedSectionIndex,
    setSelectedSectionIndex,
    sections,
    isChangedForm,
    listQuestionTypes: listQuestionTypes?.questionTypes,
    activeId,
    setActiveId,
    onClose,
    openPreviewDialog,
    setOpenPreviewDialog,
    isLoading,
    // Field arrays for Sections
    sectionsArray,
    disableAddSection,
    checklists,
    // Section Actions
    handleAddSection,
    handleRemoveSection,
    handleSectionDragEnd,
    resetForm,
    resetSeq,
    // Form state
    isError,
    // Error handling
    hasSectionError
  };
};
